import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import { Plus, SignOut, ArrowRight, X, DoorOpen, Sun, Moon, Rows, PaperPlaneTilt, Gear, Trash, Archive, ArchiveBox, Copy, Check, CurrencyDollar, CheckSquare, Calendar, CalendarCheck, Lightning, List as MenuIcon } from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";
import FilterPills from "@/components/FilterPills";

function MobileNavMenu({ items, onToggleTheme, theme, open, setOpen }) {
  return (
    <div className="relative md:hidden" data-testid="admin-mobile-menu">
      <button
        data-testid="admin-mobile-menu-btn"
        onClick={() => setOpen(o => !o)}
        aria-label="Menu"
        className="btn-ghost p-2">
        {open ? <X size={18} weight="bold" /> : <MenuIcon size={18} weight="bold" />}
      </button>
      {open && (
        <div
          className="fixed left-2 right-2 top-16 z-50 max-h-[75vh] overflow-y-auto"
          style={{ background: "var(--panel)", border: "1px solid var(--hair)",
                   boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
          data-testid="admin-mobile-menu-panel">
          <button
            onClick={() => { onToggleTheme(); setOpen(false); }}
            className="w-full text-left px-4 py-3 hover:bg-black/5 flex items-center gap-3">
            {theme === "dark" ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
            <span>Toggle theme</span>
          </button>
          {items.map(it => (
            <Link key={it.to} to={it.to} onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${it.testid}`}
              className="w-full text-left px-4 py-3 hover:bg-black/5 flex items-center gap-3 border-t"
              style={{ borderColor: "var(--hair)", color: "inherit", textDecoration: "none" }}>
              {it.icon}
              <span>{it.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, testid, to }) {
  const body = (
    <div className={`panel p-6 ${to ? "hover:opacity-80 cursor-pointer transition-opacity" : ""}`}
      data-testid={testid}>
      <p className="label">{label}</p>
      <p className="title-xl text-4xl mt-2">{value}</p>
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="no-underline block" style={{ color: "inherit" }}>
        {body}
      </Link>
    );
  }
  return body;
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "Never";
  const diff = Date.now() - then;
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

export function CreatedCredentialsModal({ result, onClose }) {
  const [copiedPw, setCopiedPw] = useState(false);
  const [copiedBoth, setCopiedBoth] = useState(false);
  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch { toast.error("Copy failed — select manually"); }
  };
  const sent = result.welcome_email === "sent";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="created-credentials-modal">
      <div className="panel w-full max-w-lg p-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="label">Client created</p>
            <h3 className="title-xl text-2xl mt-1">{result.name}</h3>
          </div>
          <button onClick={onClose} data-testid="created-modal-close" className="btn-ghost p-2"><X size={16} /></button>
        </div>
        <p className="text-sm mb-5" style={{ color: "var(--text-dim)" }}>
          {sent
            ? "A welcome email with these credentials was sent. Save a copy below just in case."
            : "The welcome email failed to send. Copy these credentials and share them manually."}
        </p>
        <div className="space-y-3">
          <div>
            <p className="label mb-1">Email</p>
            <div className="hair p-3 flex items-center justify-between gap-3">
              <code className="text-sm font-mono select-all" data-testid="created-email">{result.email}</code>
              <button data-testid="copy-email" onClick={() => copy(result.email, setCopiedBoth)}
                className="btn-ghost text-xs shrink-0">
                {copiedBoth ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                {copiedBoth ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          <div>
            <p className="label mb-1">Temporary password</p>
            <div className="hair p-3 flex items-center justify-between gap-3">
              <code className="text-sm font-mono select-all" data-testid="created-password">{result.temporary_password}</code>
              <button data-testid="copy-password" onClick={() => copy(result.temporary_password, setCopiedPw)}
                className="btn-ghost text-xs shrink-0">
                {copiedPw ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                {copiedPw ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
        <p className="label mt-4" style={{ color: "var(--text-dim)" }}>
          The client should change this password from Preferences after their first login.
        </p>
        <button data-testid="created-modal-done" onClick={onClose}
          className="btn-primary w-full justify-center h-12 mt-5">
          Done
        </button>
      </div>
    </div>
  );
}

function CreateClientModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", business_name: "", phone: "", email: "", password: "",
    client_types: ["branding"], template_id: "" });
  const [tpls, setTpls] = useState([]);
  useEffect(() => {
    api.get("/admin/templates", { params: { kind: "onboarding" } }).then(({ data }) => {
      setTpls(data);
      const def = data.find(t => t.is_default);
      if (def) setF(prev => ({ ...prev, template_id: prev.template_id || def.template_id }));
    }).catch(() => {});
  }, []);
  const toggleType = (k) => setF(prev => {
    const has = prev.client_types.includes(k);
    const next = has ? prev.client_types.filter(x => x !== k) : [...prev.client_types, k];
    return { ...prev, client_types: next.length ? next : prev.client_types };
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/clients", {
        name: f.name,
        business_name: f.business_name.trim() || undefined,
        phone: f.phone.trim() || undefined,
        email: f.email,
        password: f.password || undefined,
        client_types: f.client_types,
        template_id: f.template_id || undefined,
      });
      onCreated();
      nav(`/admin/clients/${data.user_id}`, { state: { created: data } });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  if (created) {
    return <CreatedCredentialsModal result={created} onClose={() => { setCreated(null); onClose(); }} />;
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="create-client-modal">
      <div className="panel w-full max-w-lg p-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="label">New</p>
            <h3 className="title-xl text-2xl mt-1">Create client</h3>
          </div>
          <button onClick={onClose} data-testid="modal-close-btn" className="btn-ghost p-2"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full name" value={f.name}
            data-testid="new-client-name" onChange={(e) => setF({ ...f, name: e.target.value })}
            className="w-full px-4 py-3" />
          <input placeholder="Business name (optional)" value={f.business_name}
            data-testid="new-client-business" onChange={(e) => setF({ ...f, business_name: e.target.value })}
            className="w-full px-4 py-3" />
          <input type="tel" placeholder="Phone (optional)" value={f.phone}
            data-testid="new-client-phone" onChange={(e) => setF({ ...f, phone: e.target.value })}
            className="w-full px-4 py-3" />
          <input required type="email" placeholder="Email" value={f.email}
            data-testid="new-client-email" onChange={(e) => setF({ ...f, email: e.target.value })}
            className="w-full px-4 py-3" />
          <PasswordInput placeholder="Optional password (auto-generated if empty)" value={f.password}
            testid="new-client-password" onChange={(e) => setF({ ...f, password: e.target.value })}
          />
          <div>
            <p className="label mb-1">Client type</p>
            <div className="flex flex-wrap gap-2" data-testid="new-client-types">
              {[["branding", "Branding"], ["web_design", "Web Design"], ["marketing", "Marketing"]].map(([k, label]) => {
                const on = f.client_types.includes(k);
                return (
                  <button type="button" key={k} onClick={() => toggleType(k)}
                    data-testid={`new-client-type-${k}`} aria-pressed={on}
                    className="btn-ghost text-xs h-9"
                    style={on ? { background: "var(--text)", color: "var(--bg)", borderColor: "var(--text)" } : { color: "var(--text)" }}>
                    {on ? "✓ " : ""}{label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="label mb-1">Task template</p>
            <select value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })}
              data-testid="new-client-template" className="w-full px-4 py-3">
              <option value="">Default tasks</option>
              {tpls.map(t => <option key={t.template_id} value={t.template_id}>{t.name}{t.is_default ? " (default)" : ""}</option>)}
            </select>
          </div>
          {err && <p className="label px-3 py-2" style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>{err}</p>}
          <button data-testid="submit-new-client" disabled={busy} className="w-full btn-primary justify-center h-12">
            {busy ? "Creating…" : "Create client"} <ArrowRight size={14} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const cached = getCached("admin-dashboard");
  const [stats, setStats] = useState(cached?.stats ?? null);
  const [clients, setClients] = useState(cached?.clients ?? []);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("active"); // "active" | "archived"
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/admin/stats"),
      api.get(`/admin/clients?include_archived=true`),
    ]);
    setStats(s.data);
    setClients(c.data);
    setSelected(new Set());
    setCached("admin-dashboard", { stats: s.data, clients: c.data });
  };

  // Fire-and-forget prefetch when the admin hovers a client row. Populates the
  // per-client cache so opening the profile feels instant. Never re-fetches
  // if a fresh copy is already cached in this session, and never blocks the UI.
  const prefetchInFlight = useRef(new Set());
  const prefetchClient = (userId) => {
    if (!userId) return;
    const key = `admin-client-${userId}`;
    if (getCached(key)) return;
    if (prefetchInFlight.current.has(userId)) return;
    prefetchInFlight.current.add(userId);
    Promise.all([
      api.get(`/admin/clients/${userId}`),
      api.get(`/admin/clients/${userId}/messages`),
      api.get(`/admin/clients/${userId}/files`),
      api.get(`/admin/tasks`, { params: { client_user_id: userId } }),
    ]).then(([cli, msg, fls, at]) => {
      setCached(key, {
        c: cli.data, messages: msg.data, files: fls.data,
        adminTasks: at.data || [],
      });
    }).catch(() => {})
      .finally(() => { prefetchInFlight.current.delete(userId); });
  };
  useEffect(() => { load(); }, []);

  const toggleOne = (uid) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };
  const [typeFilter, setTypeFilter] = useState("all"); // all|branding|marketing|web_design|general|null
  const [search, setSearch] = useState("");
  // Sort state — default: "due_by_agency" desc so admins see what's due first
  const [sort, setSort] = useState({ key: "due_by_agency", dir: "desc" });
  const toggleSort = (key, defaultDir = "asc") => {
    setSort(prev => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { key, dir: defaultDir };
    });
  };
  const _clientTypes = (c) => (
    Array.isArray(c.client_types) && c.client_types.length > 0
      ? c.client_types
      : (c.client_type ? [c.client_type] : [])
  ).map(t => (t || "").toLowerCase());
  const _isStatusEligible = (_c) => true; // every client participates in the lifecycle now
  const PROJECT_STATUS_META = {
    onboarding:  { label: "Onboarding",         color: "#6366f1" },
    working:     { label: "Working on Project", color: "#0ea5e9" },
    edits:       { label: "Edit Phase",         color: "#d97706" },
    active_management: { label: "Active Management", color: "#0ea5e9" },
    paused:      { label: "Paused Management",  color: "#d97706" },
    offboarding: { label: "Offboarding",        color: "#059669" },
    completed:   { label: "Completed",          color: "#64748b" },
  };
  const _matchesSearch = (c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.business_name, c.email, c.phone]
      .some(v => v && String(v).toLowerCase().includes(q));
  };
  const visibleClients = clients
    .filter(c => (view === "archived" ? !!c.archived : !c.archived))
    .filter(c => {
      if (typeFilter === "all") return true;
      // Project status filters — all clients participate in the lifecycle
      if (Object.keys(PROJECT_STATUS_META).includes(typeFilter)) {
        return (c.project_status || "onboarding") === typeFilter;
      }
      const t = _clientTypes(c);
      return t.includes(typeFilter);
    })
    .filter(_matchesSearch);
  // Sort AFTER filtering so the user always sees the sorted view
  const _statusLabel = (c) => (_isStatusEligible(c)
    ? (PROJECT_STATUS_META[c.project_status]?.label || "")
    : "");
  const _sortValue = (c, key) => {
    switch (key) {
      case "name": return (c.name || "").toLowerCase();
      case "email": return (c.email || "").toLowerCase();
      case "business": return (c.business_name || "").toLowerCase();
      case "status": return _statusLabel(c).toLowerCase();
      case "due_by_agency": return Number(c.due_by_agency ?? 0);
      case "due_by_client": return Number(c.due_by_client ?? 0);
      case "needs_verification": return Number(c.progress?.needs_verification ?? 0);
      case "last_login": return c.last_login_at ? new Date(c.last_login_at).getTime() : 0;
      default: return 0;
    }
  };
  visibleClients.sort((a, b) => {
    const av = _sortValue(a, sort.key);
    const bv = _sortValue(b, sort.key);
    if (av < bv) return sort.dir === "asc" ? -1 : 1;
    if (av > bv) return sort.dir === "asc" ? 1 : -1;
    // Stable secondary tie-break by name asc so results are deterministic
    const an = (a.name || "").toLowerCase();
    const bn = (b.name || "").toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    return 0;
  });
  const typeCounts = clients
    .filter(c => (view === "archived" ? !!c.archived : !c.archived))
    .reduce((acc, c) => {
      const types = _clientTypes(c);
      types.forEach(k => { acc[k] = (acc[k] || 0) + 1; });
      if (_isStatusEligible(c)) {
        const ps = c.project_status || "onboarding";
        acc[ps] = (acc[ps] || 0) + 1;
      }
      acc.all = (acc.all || 0) + 1;
      return acc;
    }, {});
  const toggleAll = () => {
    setSelected(prev => prev.size === visibleClients.length
      ? new Set()
      : new Set(visibleClients.map(c => c.user_id)));
  };

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const labels = { delete: `Delete ${ids.length} client(s)? This cannot be undone.`,
                     archive: `Archive ${ids.length} client(s)?`,
                     unarchive: `Unarchive ${ids.length} client(s)?`,
                     reminder: `Send task reminder to ${ids.length} client(s)?` };
    if (!window.confirm(labels[action])) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/clients/bulk", { action, client_ids: ids });
      const errs = data.errors?.length || 0;
      toast.success(`${data.processed} client(s) ${action}d${errs ? ` · ${errs} skipped` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBulkBusy(false); }
  };

  const enableOffAll = async () => {
    if (!window.confirm("Enable offboarding for ALL clients?")) return;
    try {
      const { data } = await api.post("/admin/offboarding/enable-all");
      toast.success(`Offboarding enabled for ${data.enabled_for} clients`);
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/reminders/run-now");
      toast.success(`Sent ${data.sent} reminder email(s)`);
    } catch (_e) { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-dashboard">
      <header className="border-b" style={{ borderColor: "var(--hair)" }}>
        {/* Row 1: brand + email + logout */}
        <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 md:py-4">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-semibold" style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>P</div>
            <div className="min-w-0">
              <p className="font-medium leading-none truncate">PrettyGrlsDesign</p>
              <p className="label leading-none mt-1">Admin Console</p>
            </div>
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            {user?.email && (
              <span data-testid="admin-header-email"
                className="label hidden sm:inline truncate"
                style={{ maxWidth: "50vw", color: "var(--text-dim)" }}>
                {user.email}
              </span>
            )}
            <button data-testid="admin-logout-btn" onClick={logout} className="btn-ghost text-xs whitespace-nowrap">
              <SignOut size={14} weight="bold" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
        {/* Row 2: nav — hamburger on mobile, full row on md+ */}
        <div className="flex items-center gap-2 px-4 md:px-6 pb-3 md:pb-4">
          <NotificationBell testid="admin-notif-bell" viewAllHref="/admin/notifications" />
          <MobileNavMenu
            open={mobileNavOpen} setOpen={setMobileNavOpen}
            theme={theme} onToggleTheme={toggle}
            items={[
              { to: "/admin/tasks", label: "Tasks", icon: <CheckSquare size={16} weight="bold" />, testid: "tasks" },
              { to: "/admin/projects", label: "Projects", icon: <Calendar size={16} weight="bold" />, testid: "projects" },
              { to: "/admin/queue", label: "Due List", icon: <Lightning size={16} weight="bold" />, testid: "queue" },
              { to: "/admin/appointments", label: "Appointments", icon: <CalendarCheck size={16} weight="bold" />, testid: "appointments" },
              { to: "/admin/revenue", label: "Revenue", icon: <CurrencyDollar size={16} weight="bold" />, testid: "revenue" },
              { to: "/admin/templates", label: "Templates", icon: <Rows size={16} weight="bold" />, testid: "templates" },
              { to: "/admin/settings", label: "Settings", icon: <Gear size={16} weight="bold" />, testid: "settings" },
            ]} />
          <div className="hidden md:flex flex-wrap items-center gap-2">
            <button data-testid="theme-toggle" onClick={toggle} className="btn-ghost p-2" title="Toggle theme">
              {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
            </button>
            <Link data-testid="tasks-link" to="/admin/tasks" className="btn-ghost text-xs">
              <CheckSquare size={14} weight="bold" /> Tasks
            </Link>
            <Link data-testid="projects-link" to="/admin/projects" className="btn-ghost text-xs">
              <Calendar size={14} weight="bold" /> Projects
            </Link>
            <Link data-testid="queue-link" to="/admin/queue" className="btn-ghost text-xs">
              <Lightning size={14} weight="bold" /> Due List
            </Link>
            <Link data-testid="appointments-link" to="/admin/appointments" className="btn-ghost text-xs">
              <CalendarCheck size={14} weight="bold" /> Appointments
            </Link>
            <Link data-testid="revenue-link" to="/admin/revenue" className="btn-ghost text-xs">
              <CurrencyDollar size={14} weight="bold" /> Revenue
            </Link>
            <Link data-testid="templates-link" to="/admin/templates" className="btn-ghost text-xs">
              <Rows size={14} weight="bold" /> Templates
            </Link>
            <Link data-testid="settings-link" to="/admin/settings" className="btn-ghost text-xs">
              <Gear size={14} weight="bold" /> Settings
            </Link>
          </div>
        </div>
      </header>

      <section className="p-6 md:p-6">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
          <div>
            <p className="label">Operational summary</p>
            <h1 className="title-xl text-5xl md:text-6xl mt-1">Overview.</h1>
          </div>
          <div className="flex gap-3">
            <button data-testid="add-client-btn" onClick={() => setOpen(true)} className="btn-primary">
              <Plus size={14} weight="bold" /> New client
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Clients" value={stats.clients} testid="stat-clients" />
            <StatCard label="Due by agency" value={stats.due_by_agency} testid="stat-due-agency" to="/admin/queue?filter=agency" />
            <StatCard label="Due by client" value={stats.due_by_client} testid="stat-due-client" to="/admin/queue?filter=client" />
            <StatCard label="Needs verification" value={stats.needs_verification} testid="stat-needs" to="/admin/queue?filter=verification" />
          </div>
        )}

        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
            <h2 className="title-xl text-3xl">Clients</h2>
            <div className="w-full md:w-auto">
              <FilterPills
                testid="clients-tab"
                value={view}
                onChange={(k) => { setView(k); setSelected(new Set()); }}
                options={[
                  { k: "active", label: `Active (${clients.filter(c => !c.archived).length})` },
                  { k: "archived", label: `Archived (${clients.filter(c => c.archived).length})` },
                ]}
              />
            </div>
          </div>
          <div className="mb-3">
            <input
              type="search"
              data-testid="clients-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, business, email or phone…"
              className="w-full px-4 py-2.5 text-sm"
              autoComplete="off"
            />
          </div>
          <div className="mb-4">
            <FilterPills
              testid="clients-type"
              value={typeFilter}
              onChange={(k) => { setTypeFilter(k); setSelected(new Set()); }}
              options={[
                { k: "all", label: `All (${typeCounts.all || 0})` },
                { k: "branding", label: `Branding (${typeCounts.branding || 0})` },
                { k: "marketing", label: `Marketing (${typeCounts.marketing || 0})` },
                { k: "web_design", label: `Web design (${typeCounts.web_design || 0})` },
                { k: "onboarding", label: `Onboarding (${typeCounts.onboarding || 0})` },
                { k: "working", label: `Working on Project (${typeCounts.working || 0})` },
                { k: "edits", label: `Edit Phase (${typeCounts.edits || 0})` },
                { k: "active_management", label: `Active Management (${typeCounts.active_management || 0})` },
                { k: "paused", label: `Paused (${typeCounts.paused || 0})` },
                { k: "offboarding", label: `Offboarding (${typeCounts.offboarding || 0})` },
                { k: "completed", label: `Completed (${typeCounts.completed || 0})` },
              ]}
            />
          </div>
          <div className="panel overflow-x-auto" data-testid="clients-table">
            {selected.size > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b"
                   style={{ borderColor: "var(--hair)", background: "var(--panel-2)" }}
                   data-testid="bulk-toolbar">
                <p className="label"><strong>{selected.size}</strong> selected</p>
                <div className="flex flex-wrap gap-2">
                  {view === "active" && (
                    <button data-testid="bulk-reminder" disabled={bulkBusy}
                      onClick={() => runBulk("reminder")} className="btn-ghost text-xs">
                      <PaperPlaneTilt size={12} weight="bold" /> Send task reminder
                    </button>
                  )}
                  {view === "active" ? (
                    <button data-testid="bulk-archive" disabled={bulkBusy}
                      onClick={() => runBulk("archive")} className="btn-ghost text-xs">
                      <Archive size={12} weight="bold" /> Archive
                    </button>
                  ) : (
                    <button data-testid="bulk-unarchive" disabled={bulkBusy}
                      onClick={() => runBulk("unarchive")} className="btn-ghost text-xs">
                      <Archive size={12} weight="bold" /> Unarchive
                    </button>
                  )}
                  <button data-testid="bulk-delete" disabled={bulkBusy}
                    onClick={() => runBulk("delete")} className="btn-primary text-xs">
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                  <button data-testid="bulk-clear" onClick={() => setSelected(new Set())} className="btn-ghost text-xs">
                    <X size={12} /> Clear
                  </button>
                </div>
              </div>
            )}
            <table className="w-full min-w-[820px] text-left">
              <thead style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>
                <tr>
                  <th className="label p-4 w-10">
                    <input type="checkbox" data-testid="select-all"
                      checked={selected.size > 0 && selected.size === visibleClients.length}
                      onChange={toggleAll} className="w-4 h-4 cursor-pointer" />
                  </th>
                  {[
                    { k: "name", label: "Name", defaultDir: "asc" },
                    { k: "email", label: "Email", defaultDir: "asc" },
                    { k: "business", label: "Business", defaultDir: "asc" },
                    { k: "status", label: "Status", defaultDir: "asc" },
                    { k: "due_by_agency", label: "Due by agency", defaultDir: "desc" },
                    { k: "due_by_client", label: "Due by client", defaultDir: "desc" },
                    { k: "needs_verification", label: "Needs verification", defaultDir: "desc" },
                    { k: "last_login", label: "Last login", defaultDir: "desc" },
                  ].map(col => {
                    const active = sort.key === col.k;
                    const arrow = !active ? "" : sort.dir === "asc" ? " ▲" : " ▼";
                    return (
                      <th key={col.k} className="label p-4">
                        <button type="button"
                          data-testid={`sort-${col.k}`}
                          onClick={() => toggleSort(col.k, col.defaultDir)}
                          className="label inline-flex items-center gap-1 hover:opacity-80"
                          style={{
                            background: "transparent", border: "none",
                            color: "inherit", cursor: "pointer",
                            fontWeight: active ? 800 : 600,
                            textTransform: "inherit",
                            letterSpacing: "inherit",
                            fontSize: "inherit",
                          }}
                          title={active ? `Sorted ${sort.dir}` : "Click to sort"}>
                          {col.label}{arrow}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleClients.length === 0 && (
                  <tr><td colSpan={9} className="p-8 text-center label">
                    {view === "archived" ? "No archived clients" : "No clients yet"}
                  </td></tr>
                )}
                {visibleClients.map((c) => (
                  <tr key={c.user_id}
                    className="border-t cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderColor: "var(--hair)" }}
                    data-testid={`client-row-${c.user_id}`}
                    onMouseEnter={() => prefetchClient(c.user_id)}
                    onFocus={() => prefetchClient(c.user_id)}
                    onClick={() => nav(`/admin/clients/${c.user_id}`)}>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox"
                        data-testid={`select-${c.user_id}`}
                        checked={selected.has(c.user_id)}
                        onChange={() => toggleOne(c.user_id)}
                        className="w-4 h-4 cursor-pointer" />
                    </td>
                    <td className="p-4 font-medium">{c.name}</td>
                    <td className="p-4" style={{ color: "var(--text-dim)" }}>{c.email}</td>
                    <td className="p-4" data-testid={`biz-${c.user_id}`}>
                      {c.business_name || <span style={{ color: "var(--text-dim)" }}>—</span>}
                    </td>
                    <td className="p-4" data-testid={`project-status-${c.user_id}`}>
                      {_isStatusEligible(c) ? (() => {
                        const meta = PROJECT_STATUS_META[c.project_status] || PROJECT_STATUS_META.onboarding;
                        return (
                          <span className="label px-2 py-1 inline-flex items-center gap-1.5"
                            style={{
                              background: `${meta.color}22`,
                              color: meta.color,
                              border: `1px solid ${meta.color}55`,
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}>
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: meta.color }} />
                            {meta.label}
                          </span>
                        );
                      })() : (
                        <span style={{ color: "var(--text-dim)" }}>—</span>
                      )}
                    </td>
                    <td className="p-4" data-testid={`due-agency-${c.user_id}`}>{c.due_by_agency ?? 0}</td>
                    <td className="p-4" data-testid={`due-client-${c.user_id}`}>{c.due_by_client ?? 0}</td>
                    <td className="p-4">{c.progress.needs_verification}</td>
                    <td className="p-4 label" data-testid={`last-login-${c.user_id}`} title={c.last_login_at || ""}>
                      {timeAgo(c.last_login_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {open && <CreateClientModal onClose={() => setOpen(false)} onCreated={load} />}
    </div>
  );
}
